import { type AnyEventName, type EventPayload, type WebSDKEvents } from "@sumsub/websdk/types/types";
import { makeAutoObservable, runInAction } from "mobx";
import type { AddressVerificationStore } from "services/addressVerification/addressVerificationStore";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { FeatureStore } from "services/feature/featureStore";
import type { IdentityQuestionsStore } from "services/questions/gb/identityQuestionsStore";
import { type SumsubClient } from "services/sumsub/sumsubClient";
import { SumsubCheckFlowName } from "services/sumsub/sumsubTypes";
import { type SumsubToken } from "./types";

const MAX_POA_CHECKS = 10;

type DirectorPoaWidgetState = "waiting_for_bank" | "waiting_for_poa" | "success" | "error";

export class DirectorPoaWidgetStore {
  private _state: DirectorPoaWidgetState = "waiting_for_bank";
  private _token: SumsubToken = undefined;
  private _poaCheckCounter = 0;

  constructor(
    private readonly _sumsubClient: SumsubClient,
    private readonly _addressVerificationStore: AddressVerificationStore,
    private readonly _identityQuestionsStore: IdentityQuestionsStore,
    private readonly _featureStore: FeatureStore,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    makeAutoObservable(this);
  }

  get token(): SumsubToken {
    return this._token;
  }

  private set token(value: SumsubToken) {
    this._token = value;
  }

  get state(): DirectorPoaWidgetState {
    return this._state;
  }

  set state(value: DirectorPoaWidgetState) {
    this._state = value;
  }

  async init(): Promise<void> {
    await this.getSumsubToken();
  }

  async getSumsubToken(): Promise<string> {
    const token = await this._sumsubClient.getSumsubToken(SumsubCheckFlowName.ONE_CLICK_LEVEL);
    runInAction(() => {
      this.token = token;
      this._analyticsManager.event("sumsub-poa-token.generated");
    });
    return token;
  }

  async onMessage(message: AnyEventName, payload: EventPayload<AnyEventName>): Promise<void> {
    this._analyticsManager.event(`sumsub-poa-message.${message}`, payload);

    if (this._featureStore.hasFeature("formationSumsubSdkV2")) {
      await this._onMessageV2(message, payload);
    } else {
      await this._onMessageV1(message);
    }
  }

  async _onMessageV1(message: AnyEventName): Promise<void> {
    switch (message) {
      case "idCheck.applicantReviewComplete":
        await this._onApplicantReviewComplete();
        break;
    }
  }

  async _onMessageV2(message: AnyEventName, payload: EventPayload<AnyEventName>): Promise<void> {
    switch (message) {
      case "idCheck.onApplicantStatusChanged":
        await this._onApplicantStatusChanged(payload as WebSDKEvents["idCheck.onApplicantStatusChanged"]);
        break;
    }
  }

  private async _onApplicantReviewComplete(): Promise<void> {
    this.state = "waiting_for_poa";
    const interval = setInterval(async () => {
      if (this._poaCheckCounter === MAX_POA_CHECKS) {
        this.state = "error";
        clearInterval(interval);
        this._identityQuestionsStore.onOpenbankingCheckError();
        return;
      }

      this._poaCheckCounter += 1;
      const poaIsRequired = await this._addressVerificationStore.checkDirectorAddress();

      if (!poaIsRequired) {
        this.state = "success";
        clearInterval(interval);
      }
    }, 3000);
  }

  private async _onApplicantStatusChanged(payload: WebSDKEvents["idCheck.onApplicantStatusChanged"]): Promise<void> {
    if (payload.reviewStatus !== "completed") {
      return;
    }
    await this._onApplicantReviewComplete();
  }
}
